import React from "react";
import { Button, Switch, Tooltip } from "antd";
import { Eye, EyeOff, Bell, Sparkles, Loader2, Lock, Unlock } from "lucide-react";

/**
 * CommandCenterConfig — declarative control configuration for ConsensusBuilderBar.
 *
 * Each surface (Progress, QO, future BBs) passes a config object describing which
 * controls to render and their current state. ConsensusBuilderBar renders the standard
 * control set from this config — surfaces never pass raw JSX for controls.
 */
export interface CommandCenterConfig {
  visibility?: {
    isVisible: boolean;
    onToggle: () => void;
    tooltipVisible?: string;
    tooltipHidden?: string;
  };
  notify?: {
    onClick: () => void;
  };
  synthesize?: {
    onClick: () => void;
    isSynthesizing: boolean;
  };
  lock?: {
    isLocked: boolean;
    onToggle: () => void;
  };
}

/**
 * PillToggle — the shared Solo/Team switcher used wherever Consensus Building is active.
 * Dark purple pill, white selected state, identical across Progress & Reflection and
 * Quarterly Objectives. Single source of truth — import from here, don't redefine locally.
 */
export function PillToggle({
  value,
  onChange,
  testId,
}: {
  value: "solo" | "team";
  onChange: (v: "solo" | "team") => void;
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      className="flex items-center rounded-full p-0.5 shrink-0"
      style={{
        background: "#1a1040",
        border: "1.5px solid #2c1561",
        boxShadow: "0 0 0 1px rgba(44,21,97,0.4)",
      }}
    >
      {(["solo", "team"] as const).map((v) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          className={`border-0 outline-none cursor-pointer px-3 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all ${
            value === v
              ? "bg-white text-[#1a1040]"
              : "bg-transparent text-white/75 hover:text-white"
          }`}
        >
          {v}
        </button>
      ))}
    </div>
  );
}

/**
 * Renders the standard O/A control set from a CommandCenterConfig.
 * Ordering: Visibility → Notify → Synthesize → Release → Lock
 */
function CommandCenterControls({ config }: { config: CommandCenterConfig }) {
  return (
    <>
      {config.visibility && (
        <Tooltip
          title={
            config.visibility.isVisible
              ? (config.visibility.tooltipVisible ?? "Team data visible to contributors — click to hide")
              : (config.visibility.tooltipHidden ?? "Team data hidden from contributors — click to show")
          }
        >
          <div className="flex items-center gap-1 cursor-pointer" onClick={config.visibility.onToggle}>
            {config.visibility.isVisible
              ? <Eye className="h-3 w-3 text-emerald-600" />
              : <EyeOff className="h-3 w-3 text-amber-500" />
            }
            <Switch
              size="small"
              checked={config.visibility.isVisible}
              onChange={config.visibility.onToggle}
              style={{ backgroundColor: config.visibility.isVisible ? "#10b981" : "#f59e0b" }}
            />
          </div>
        </Tooltip>
      )}

      {config.notify && (
        <Button size="small" icon={<Bell className="h-3 w-3" />} onClick={config.notify.onClick} style={{ fontSize: 11 }}>
          Notify Team
        </Button>
      )}

      {config.synthesize && (
        <Button
          size="small"
          icon={config.synthesize.isSynthesizing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
          onClick={config.synthesize.onClick}
          disabled={config.synthesize.isSynthesizing}
          style={{ fontSize: 11 }}
        >
          {config.synthesize.isSynthesizing ? "Synthesizing\u2026" : "Synthesize with Vo"}
        </Button>
      )}

      {config.lock && (
        <Tooltip title={config.lock.isLocked ? "Unlock this period" : "Lock this period"}>
          <Button
            size="small"
            icon={config.lock.isLocked ? <Unlock className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
            onClick={config.lock.onToggle}
            style={{ fontSize: 11 }}
          >
            {config.lock.isLocked ? "Unlock" : "Lock"}
          </Button>
        </Tooltip>
      )}
    </>
  );
}

/**
 * ConsensusBuilderBar — the unified "Command Center" control bar.
 *
 * Layout: [leftContent?] [O/A controls (config-driven)] | divider | [Solo/Team toggle (right)]
 *
 * Non-O/A users: only the PillToggle renders (plus optional leftContent).
 * O/A users: config-driven controls + divider + toggle.
 *
 * Use this component wherever Consensus Building is deployed so the experience
 * is 100% consistent across Progress & Reflection, Quarterly Objectives, and
 * any future Consensus Building surface.
 */
export function ConsensusBuilderBar({
  isAdvisorOrOwner,
  viewMode,
  onViewModeChange,
  showToggle = true,
  toggleTestId,
  controls,
  leftContent,
  className,
}: {
  isAdvisorOrOwner?: boolean;
  /** Optional: only needed when showToggle is true. */
  viewMode?: "solo" | "team";
  onViewModeChange?: (v: "solo" | "team") => void;
  /** When false, the Solo/Team pill is suppressed (a global changer owns it elsewhere). Default true. */
  showToggle?: boolean;
  /** Forwarded to the rendered PillToggle for e2e targeting. */
  toggleTestId?: string;
  /** Config-driven O/A controls. Replaces the old advisorControls JSX slot. */
  controls?: CommandCenterConfig;
  /** Optional content rendered on the far left (e.g., sub-tabs in Progress). */
  leftContent?: React.ReactNode;
  className?: string;
}) {
  const hasControls = isAdvisorOrOwner && controls && Object.keys(controls).length > 0;
  const renderToggle = showToggle && viewMode !== undefined && onViewModeChange !== undefined;

  if (!hasControls && !renderToggle && !leftContent) return null;

  return (
    <div
      className={`flex items-center gap-2 px-2 py-1.5 border-b bg-muted/20 shrink-0 ${className ?? ""}`}
    >
      {leftContent}
      <div className="flex items-center gap-2 ml-auto">
        {hasControls && (
          <>
            <CommandCenterControls config={controls} />
            {renderToggle && <span className="text-slate-300 text-[11px] select-none">|</span>}
          </>
        )}
        {renderToggle && (
          <PillToggle value={viewMode} onChange={onViewModeChange} testId={toggleTestId} />
        )}
      </div>
    </div>
  );
}
